import { definedNotNull } from '../defined/index.js';

/**
 * The structural name shape both [Name] and the BankID wire `FullName`
 * satisfy. The formatters below work on it, so a method-less `FullName` can be
 * rendered without first wrapping it in a [Name].
 */
export interface NameParts {
  first: string;
  given?: string;
  last: string;
}

/** "First Last" (or just "Last" when there is no first name). */
export function displayFull(name: NameParts): string {
  if (name.first.length > 0) {
    return `${name.first} ${name.last}`;
  }

  return `${name.last}`;
}

/** "Given Last" when a given name is set, otherwise [displayFull]. */
export function displayName(name: NameParts): string {
  if (definedNotNull(name.given)) {
    return `${name.given} ${name.last}`;
  }

  return displayFull(name);
}

/** The selected given name when known, otherwise all first names. */
function preferredFirst(name: NameParts): string {
  return definedNotNull(name.given) ? name.given : name.first;
}

/** First letter of the preferred first name and of the surname, e.g. "AA". */
export function initials(name: NameParts): string {
  // Spread to code points so an initial outside the BMP isn't split in half.
  const first = [...preferredFirst(name).trim()].at(0) ?? '';
  const last = [...name.last.trim()].at(0) ?? '';
  return `${first}${last}`;
}

export class Name implements NameParts {
  first: string;
  given: string | undefined;
  last: string;
  constructor(props: { first: string; given?: string; last: string }) {
    this.first = props.first;
    this.given = props.given;
    this.last = props.last;
  }

  toJSON(): { first: string; given: string | undefined; last: string } {
    return {
      first: this.first,
      given: this.given,
      last: this.last,
    };
  }

  public displayFull() {
    return displayFull(this);
  }

  public displayName() {
    return displayName(this);
  }

  public initials() {
    return initials(this);
  }

  static fromString(s: string): Name {
    const [last, ...firstArray] = s
      .split(' ')
      .filter((s) => s.length > 0)
      .reverse();
    return new Name({
      first: firstArray.reverse().join(' '),
      given: firstArray.length === 1 ? firstArray.at(0) : undefined,
      last,
    });
  }
}
